import { getExtraHTML } from '~/actions/extraHTML';
import { configure, iterator, _ } from '~/actions/triggers/generalNotification';
import { getCustomCSS } from '~/actions/triggers/getCustomCSS';
import { getResetCss } from '~/actions/triggers/resetCss';
import { NotificationConfig } from '~/actions/triggers/types';
import { getUserNotificationStyles } from '~/actions/triggers/userNotificationStyles';

enum Months {
  'января' = 1,
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
}

interface CheckboxProps {
  text: string;
  className?: string;
  squareStyles?: string;
  textStyles?: string;
  isChecked?: boolean;
}

export const run = (
  scenarioName: string,
  configuration: NotificationConfig,
  extraHTMLParams: any
) => {
  const {
    position = 'center',
    uuid = '',
    imageSize = 'original',
    imagePosition = 'center',
    buttonHREF,
    buttonSize = 'large',
    buttonText,
    isButtonBlockWidth = true,
    checkboxColor,
    coupon,
    expirationDate,
    hasCoupon,
    hasExpirationDate,
    hasImage,
    hasInput,
    hasMessage,
    hasPrivacyPolicy,
    isHorizontal,
    inputPlaceholder,
    message,
    privacyPolicyLinkText,
    privacyPolicyOpacity,
    privacyPolicyText,
    title,
    hasExtraHTML = false,
  } = configuration;

  let isMobile = configuration.isMobile;
  let alignment = configuration.alignment || 'bottom-center';

  let extraHTMLObject;
  let extraCSS = '';
  let extraHTML = '';
  let beforeHTML = false;
  let afterHTML = false;
  let notificationWrapClass = '';

  if (hasExtraHTML) {
    extraHTMLObject = getExtraHTML(configuration['@type'], extraHTMLParams, configuration);
    extraCSS = extraHTMLObject.css;
    extraHTML = extraHTMLObject.html;
    beforeHTML = extraHTMLObject.isBefore;
    afterHTML = extraHTMLObject.isAfter;
    notificationWrapClass = extraHTMLObject.notificationWrapClass;
    isMobile = extraHTMLObject.isMobile;
    if (isMobile) {
      alignment = configuration.alignmentMobile;
    }
  }

  const dateArr = expirationDate?.split('-');
  const month = Months[Number(dateArr?.[1])];
  const formattedExpirationDate = `До ${dateArr?.[2]} ${month} ${dateArr?.[0]}`;

  const renderCloseIcon = (className?: string) => `
    <svg viewBox="0 0 24 24" fill="none" ${
    className ? `class='${className}'` : ''
  }>
      <path d="M17.303 6.696L12 11.999m-5.303 5.303L12 12m0 0L6.697 6.696M12 11.999l5.303 5.303" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
    </svg>
  `;

  const clockIcon = `<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="7" cy="7" r="6.5" stroke="currentColor"/>
  <path d="M6.5 3.5C6.5 3.22386 6.72386 3 7 3C7.27614 3 7.5 3.22386 7.5 3.5V8C7.5 8.27614 7.27614 8.5 7 8.5C6.72386 8.5 6.5 8.27614 6.5 8V3.5Z" fill="currentColor"/>
  <path d="M10 7.5C10.2761 7.5 10.5 7.72386 10.5 8C10.5 8.27614 10.2761 8.5 10 8.5L7 8.5C6.72386 8.5 6.5 8.27614 6.5 8C6.5 7.72386 6.72386 7.5 7 7.5L10 7.5Z" fill="currentColor"/>
  </svg>`;

  const checkIcon = `
    <svg fill="none" viewBox="0 0 24 24" class="${_}icon ${_}checkbox__svg">
      <path d="M8 12.5l2.75 2.5L16 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>
  `;

  const input = `
    <div class="${_}input-wrapper _size-large ${_}input ${_}notification__input">
      <div class="${_}input-wrapper__placeholder"></div>
      <div class="${_}input-wrapper__body"><input type="text" class="${_}input__element" placeholder="${inputPlaceholder}" value=""></div>
    </div>
  `;

  const renderCheckbox = ({
                            text,
                            className,
                            squareStyles = '',
                            textStyles = '',
                            isChecked,
                          }: CheckboxProps) => `
    <div class="${_}checkbox ${
    isChecked ? '_checked' : ''
  } _size-small ${className}">
      <label class="${_}checkbox__label">
        <input class="${_}checkbox__input" type="checkbox" ${
    isChecked ? 'checked' : ''
  }>
        <span class="${_}checkbox__square" ${
    isChecked && squareStyles ? `style='${squareStyles}'` : ''
  }></span>
        <span class="${_}checkbox__icon">
          ${checkIcon}
        </span>
        <span class="${_}checkbox__text" ${
    textStyles ? `style='${textStyles}'` : ''
  }>
          ${text}
        </span>
      </label>
    </div>
  `;

  const text = `${privacyPolicyText?.slice(
    0,
    privacyPolicyText?.indexOf(privacyPolicyLinkText)
  )}
    <span class="${_}notification__privacy-policy-link">
      ${privacyPolicyLinkText}
    </span>
    ${privacyPolicyText?.slice(
    privacyPolicyText?.indexOf(privacyPolicyLinkText) +
    privacyPolicyLinkText?.length
  )}`;

  const isHorizontalFinal = isHorizontal || (extraHTMLObject && !extraHTMLObject.isMobile);

  const customHTML = `
    <div class="${_}notification__box _position-${position} _image-size-${imageSize} _image-position-${imagePosition} ${
    isHorizontalFinal ? '_horizontal' : ''
  } ${isMobile ? '_mobile' : ''} ${hasImage ? '_has-image' : ''} ${notificationWrapClass}">
    ${beforeHTML ? extraHTML : ''}
      <div class="${_}notification__close">
        ${renderCloseIcon(`${_}notification-close__icon`)}
      </div>
      <div class="${_}notification__body _alignment-${alignment}">
        <div class="${_}notification__title">${title.replaceAll('\n', '<br />')}</div>
        ${
    hasMessage
      ? `<div class="${_}notification__message">${message.replaceAll('\n', '<br />')}</div>`
      : ''
  }
        ${
    hasCoupon
      ? `
            <div class="${_}notification__coupon">
              <div class="${_}notification__coupon-text _hidden">${coupon}</div>
            </div>
        `
      : ''
  }
        ${
    hasExpirationDate
      ? `
            <div class="${_}notification__expiration">
              <div class="${_}notification__expiration-icon">${clockIcon}</div>
              <div class="${_}notification__expiration-date">${formattedExpirationDate}</div>
            </div>
        `
      : ''
  }
        ${hasInput ? input : ''}
        <${
    buttonHREF ? 'a' : 'button'
  } class="${_}notification__button _type-primary _size-${buttonSize} ${
    isButtonBlockWidth ? '_block' : ''
  }" href=${buttonHREF ? buttonHREF : ''}>
          ${buttonText}
        </${buttonHREF ? 'a' : 'button'}>
        ${
    hasPrivacyPolicy
      ? renderCheckbox({
        isChecked: true,
        text,
        className: `${_}notification__privacy-policy`,
        squareStyles: `background-color: ${checkboxColor}; border-color: ${checkboxColor}`,
        textStyles: `opacity: ${privacyPolicyOpacity}`,
      })
      : ''
  }
      </div>
      ${afterHTML ? extraHTML : ''}
    </div>
  `;

  const resetCSS = getResetCss(_, iterator);
  const customCSS = getCustomCSS({ _, iterator, isMobile, configuration });
  const userNotificationStyles = getUserNotificationStyles(iterator);
  const finalCustomCSS = resetCSS + customCSS + userNotificationStyles + extraCSS;

  return configure(
    scenarioName,
    {
      ...configuration,
      position,
      isCustom: true,
      isLightbox: true,
      uuid,
      width: isHorizontalFinal ? '800px' : isMobile ?  '320px' : '452px',
      height: !isHorizontalFinal ? '800px' : '452px',
    },
    customHTML,
    finalCustomCSS
  );
};
